import { PickPartial } from '@bpd-library/types';
import { ISO_CODE_GERMANY, ISO_CODE_NETHERLANDS } from '@constants/countries';
import environment from '@utilities/environment';
import { getAssetsFolder } from '@utilities/get-assets-folder';

const TEST_GERMANY = false;
const LANGUAGE_ISO_CODE = TEST_GERMANY ? ISO_CODE_GERMANY : ISO_CODE_NETHERLANDS;
const LOCAL_DICTIONARY_PATH = TEST_GERMANY ? '/lang/de.json' : '/lang/nl.json';

type MockBaseProps = Pick<
    __ENVIRONMENT_DETAILS__,
    | 'environment'
    | 'languageIsoCode'
    | 'dictionaryUrl'
    | 'compareUrl'
    | 'myEnvironmentUrl'
    | 'areaHouseOverviewUrl'
    | 'mapbox'
    | 'logoutUrl'
    | 'friendlyCaptchaSiteKey'
>;

const defaultProps: MockBaseProps = {
    environment: 'local',
    dictionaryUrl: getAssetsFolder(LOCAL_DICTIONARY_PATH),
    languageIsoCode: LANGUAGE_ISO_CODE,
    compareUrl: '/compare-houses',
    areaHouseOverviewUrl: '/properties-overview',
    logoutUrl: 'https://acceptance-bpd.cs189.force.com/MijnEigenHuis/secur/logout.jsp',
    myEnvironmentUrl: '/account',
    friendlyCaptchaSiteKey: 'FCMGEMUD2KTDSQ5H', // Friendly Captcha test sitekey
    mapbox: {
        style: 'bpd-acc/ciowo3k3f000udum18y8x7qd0',
        accessToken:
            'pk.eyJ1IjoiYnBkLWFjYyIsImEiOiJjaWlzbzBreXcwMzk5dTZrbnNlMDY1ZTFhIn0.N4JONLqHs9Bc67kJTikkXw',
    },
};

type EnvironmentName = 'TEST' | 'ACC' | 'PROD';

const defaultEnvProps: Record<EnvironmentName, MockBaseProps> = {
    TEST: defaultProps,
    ACC: defaultProps,
    PROD: {
        ...defaultProps,
        logoutUrl: 'https://www.mijneigenhuis.nl/secur/logout.jsp',
        mapbox: {
            style: 'bpd/cj47zzkvg1jo62roansv1k0mo',
            accessToken:
                'pk.eyJ1IjoiYnBkIiwiYSI6IjBiNTQyNmIyNTFmNTk4NDdkOTE1MDMyNDViYWRmMDI1In0.tfd0_36gpomlERjFuqPIsQ',
        },
    },
};

type EnvironmentDetailsProps = Partial<__ENVIRONMENT_DETAILS__>;
type EnvironmentMockAreaProps = PickPartial<
    __ENVIRONMENT_DETAILS__,
    'areaProjectName' | 'areaId' | 'districtHouseOverviewUrl'
> & { domain: string };
type EnvironmentMocker = (props?: EnvironmentDetailsProps) => void;
type EnvironmentMockerGenerator = (
    areaProps: EnvironmentMockAreaProps,
    envProps?: Partial<Record<EnvironmentName, EnvironmentDetailsProps>>,
) => Record<EnvironmentName, EnvironmentMocker>;

const MOCK_NODE_ID = 'mockNodeId';
const baseApiMock: Record<EnvironmentName, (domain: string) => string> = {
    TEST: (domain) => `https://${domain}.test.bpd.nl`,
    ACC: (domain) => `https://${domain}.acc.bpd.nl`,
    PROD: (domain) => `https://${domain}.nl`,
};

const createMocks: EnvironmentMockerGenerator = ({ domain, ...areaProps }, envProps) => ({
    TEST: (extraProps) => {
        environment.baseApiUrl = baseApiMock.TEST(domain);
        window.__ENVIRONMENT_DETAILS__ = {
            apiBaseUrl: `${environment.baseApiUrl}/api/middleware`,
            nodeId: MOCK_NODE_ID,
            ...defaultEnvProps.TEST,
            ...areaProps,
            ...envProps?.TEST,
            ...extraProps,
        };
    },
    ACC: (extraProps) => {
        environment.baseApiUrl = baseApiMock.ACC(domain);
        window.__ENVIRONMENT_DETAILS__ = {
            apiBaseUrl: `${environment.baseApiUrl}/api/middleware`,
            nodeId: MOCK_NODE_ID,
            ...defaultEnvProps.ACC,
            ...areaProps,
            ...envProps?.ACC,
            ...extraProps,
        };
    },
    PROD: (extraProps) => {
        environment.baseApiUrl = baseApiMock.PROD(domain);
        window.__ENVIRONMENT_DETAILS__ = {
            apiBaseUrl: `${environment.baseApiUrl}/api/middleware`,
            nodeId: MOCK_NODE_ID,
            ...defaultEnvProps.PROD,
            ...areaProps,
            ...envProps?.PROD,
            ...extraProps,
        };
    },
});

const hoefenHaag: EnvironmentMockAreaProps = {
    domain: 'hoefenhaag',
    areaId: 'a0iw0000008QvIVAA0',
    areaProjectName: 'Hoef en Haag',
    districtHouseOverviewUrl: {
        a0lw000000EBLa4AAH: '/het-dorpshart/woningen',
        a0lw000000EBvWTAA1: '/het-lint/woningen',
        a0lw000000EBvWYAA1: '/de-erven/woningen',
    },
};

const digitalPark: EnvironmentMockAreaProps = {
    domain: 'digital-park',
    areaId: 'a0i7R00001qWMD9QAO',
    areaProjectName: 'Digital Park',
    districtHouseOverviewUrl: {
        a0l7R00000dUs1zQAC: '/department-digitals/woningen',
        a0l7R00000gK6fuQAC: '/department-digital-rental/woningen',
    },
};

const AREA_PAGE_MOCKS = {
    HoefenHaag: createMocks(hoefenHaag),
    DeErven: createMocks({
        ...hoefenHaag,
        districtId: 'a0lw000000EBvWYAA1',
    }),
    DigitalPark: createMocks(digitalPark),
    DepartmentDigitalProjectPage: createMocks(digitalPark, {
        TEST: {
            districtId: 'a0l7R00000dUs1zQAC',
            projectId: 'a067R00002zmK6YQAU',
        },
    }),
    DeNieuweDefensie: createMocks({
        domain: 'nieuwbouw-denieuwedefensie',
        areaId: 'a0i2X00000Cf6aQQAR',
        districtId: 'a0l2X00000YRioKQAT',
        areaProjectName: 'De Nieuwe Defensie',
        districtHouseOverviewUrl: {
            a0l2X00000YRioKQAT: '/woningen',
        },
    }),
    Waalfront: createMocks({
        domain: 'nieuwbouw-waalfront',
        areaId: 'a0iw000000ADveXAAT',
        areaProjectName: 'Waalfront',
        districtHouseOverviewUrl: {
            a0l7R00000dUspmQAC: '/properties-overview',
        },
    }),
};

/**
 * Prefills the `extendedArea` session storage cache with `/mocks/area-data/[areaId].json`
 * for the currently mocked area. Without a local mock, area data is fetched from the API.
 */
const prefillAreaDataMock = async () => {
    const { areaId } = window.__ENVIRONMENT_DETAILS__;
    if (!areaId) return;

    try {
        const response = await fetch(getAssetsFolder(`/mocks/area-data/${areaId}.json`));
        if (!response.ok) throw new Error(`${response.status}`);

        const data = await response.json();

        sessionStorage.setItem(
            `AreaObjects::${areaId}`,
            JSON.stringify({ timestamp: Date.now(), data }),
        );
    } catch {
        console.info(`[environment-mock-data] -- no local area data mock for ${areaId}`);
    }
};

export { AREA_PAGE_MOCKS, prefillAreaDataMock };
